import { useRef, useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import Button from '../../../shared/components/Button';
import { CheckboxMultiSelect } from './CheckboxMultiSelect';
import { useUploadPortfolioMedia } from '../hooks/useCharter';
import type { CharterMedia, CharterService, CreatePortfolioWorkPayload, PortfolioWork } from '../types';

interface Props {
  initial?: PortfolioWork | null;
  services: CharterService[];
  onSubmit: (values: CreatePortfolioWorkPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function PortfolioWorkForm({ initial, services, onSubmit, onCancel, isSaving }: Props) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [caption, setCaption] = useState(initial?.caption ?? '');
  const [serviceIds, setServiceIds] = useState<string[]>(initial?.serviceIds ?? []);
  const [media, setMedia] = useState<CharterMedia[]>(initial?.media ?? []);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = useUploadPortfolioMedia();

  const serviceOptions = services.map((s) => ({ value: s.id, label: s.name }));

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const result = await upload.mutateAsync(file);
      setMedia((prev) => [...prev, { id: result.id, cdnUrl: result.cdnUrl }]);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeMedia = (id: string) => setMedia((prev) => prev.filter((m) => m.id !== id));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (media.length === 0) {
      setError('Upload at least one image or video');
      return;
    }
    onSubmit({
      title: title.trim(),
      caption: caption.trim() || undefined,
      mediaIds: media.map((m) => m.id),
      serviceIds: serviceIds.length > 0 ? serviceIds : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input label="Title" placeholder="e.g. SS26 Ivory Bridal" value={title} onChange={(e) => setTitle(e.target.value)} />
      <TextArea label="Caption" value={caption} onChange={(e) => setCaption(e.target.value)} />

      <div>
        <label className="block text-sm text-foreground mb-1">Media</label>
        <div className="flex gap-2 flex-wrap mb-2">
          {media.map((m) => (
            <div key={m.id} className="relative">
              <img src={m.cdnUrl} alt="" className="h-20 w-20 rounded-md object-cover border border-border" />
              <button
                type="button"
                onClick={() => removeMedia(m.id)}
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-xs leading-none"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="text-sm text-muted-foreground"
        />
        {upload.isPending && <p className="text-xs text-muted-foreground mt-1">Uploading…</p>}
      </div>

      <CheckboxMultiSelect
        label="Tag services (optional)"
        options={serviceOptions}
        selected={serviceIds}
        onChange={setServiceIds}
        emptyLabel="No services to tag yet."
      />

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving || upload.isPending} loading={isSaving}>
          {initial ? 'Save changes' : 'Create work'}
        </Button>
      </div>
    </form>
  );
}
